import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { pluginsApi } from '../../services/dispatchApi';
import { LoadingState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';

const SES_FIELDS = [
  { key: 'host', label: 'SMTP Host', placeholder: 'email-smtp.us-east-1.amazonaws.com' },
  { key: 'port', label: 'Port', placeholder: '587' },
  { key: 'username', label: 'Username / Access Key', placeholder: 'AKIA...', secret: true },
  { key: 'password', label: 'Password / Secret', placeholder: '••••••••', secret: true },
  { key: 'encryption', label: 'Encryption', placeholder: 'tls' },
  { key: 'from_address', label: 'From Email', placeholder: 'no-reply@digital.flare.pk' },
  { key: 'from_name', label: 'From Name', placeholder: 'Flare Magazine' },
];

const TCS_FIELDS = [
  { key: 'api_url', label: 'API Base URL', placeholder: 'https://api.tcscourier.com' },
  { key: 'api_key', label: 'API Key', placeholder: '', secret: true },
  { key: 'client_id', label: 'Client ID', placeholder: '' },
  { key: 'username', label: 'Username', placeholder: '' },
  { key: 'password', label: 'Password', placeholder: '', secret: true },
  { key: 'account_no', label: 'Account No', placeholder: '' },
  { key: 'cost_center', label: 'Cost Center Code', placeholder: '' },
];

const LEOPARDS_FIELDS = [
  { key: 'api_url', label: 'API Base URL', placeholder: 'https://merchantapi.leopardscourier.com/api/' },
  { key: 'api_key', label: 'API Key', placeholder: '', secret: true },
  { key: 'api_password', label: 'API Password', placeholder: '', secret: true },
  { key: 'origin_city', label: 'Origin city (self or numeric ID only)', placeholder: 'self' },
  { key: 'shipment_id', label: 'Shipment ID (optional)', placeholder: '' },
  { key: 'weight_grams', label: 'Default weight (grams)', placeholder: '500' },
  { key: 'shipment_type', label: 'Shipment type', placeholder: 'overnight' },
];

const JAZZCASH_FIELDS = [
  { key: 'merchant_id', label: 'Merchant ID', placeholder: 'MC991077' },
  { key: 'password', label: 'Password', placeholder: '', secret: true },
  { key: 'integrity_salt', label: 'Integrity Salt', placeholder: '', secret: true },
  { key: 'environment', label: 'Environment', placeholder: 'sandbox' },
  { key: 'return_url', label: 'Return URL', placeholder: 'https://digital.flare.pk/api/payments/jazzcash/return' },
  { key: 'ipn_url', label: 'IPN URL', placeholder: 'https://digital.flare.pk/api/payments/jazzcash/ipn' },
  { key: 'frontend_url', label: 'Frontend URL', placeholder: 'https://digital.flare.pk' },
];

/** Live vs testing presets (courier = API URL; JazzCash = environment flag) */
const ENV_PRESETS = {
  leopards: {
    field: 'api_url',
    live: 'https://merchantapi.leopardscourier.com/api/',
    testing: 'https://merchantapistaging.leopardscourier.com/api/',
  },
  tcs: {
    field: 'api_url',
    live: 'https://api.tcscourier.com',
    testing: 'https://api.tcscourier.com',
  },
  jazzcash: {
    field: 'environment',
    live: 'live',
    testing: 'sandbox',
  },
};

function detectEnvMode(value, presets) {
  const u = String(value || '').trim().toLowerCase().replace(/\/+$/, '');
  if (!u || !presets) return 'live';
  const live = String(presets.live || '').toLowerCase().replace(/\/+$/, '');
  const testing = String(presets.testing || '').toLowerCase().replace(/\/+$/, '');
  if (testing && (u === testing || u.includes('staging') || u.includes('sandbox') || u.includes('test'))) {
    return 'testing';
  }
  if (live && u === live) return 'live';
  if (u.includes('staging') || u.includes('sandbox')) return 'testing';
  return 'live';
}

function PluginCard({
  id,
  title,
  description,
  ready,
  fieldsMeta,
  fieldDefs,
  onSave,
  onTest,
  onSyncCities,
  onConnectTest,
  testLabel,
  writable,
  writeHint,
  open,
  onToggle,
  envPresets,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectResult, setConnectResult] = useState(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    const next = {};
    fieldDefs.forEach((f) => {
      const meta = fieldsMeta?.[f.key];
      next[f.key] = f.secret ? '' : (meta?.display || '');
    });
    setForm(next);
  }, [fieldsMeta, fieldDefs]);

  const envField = envPresets?.field || 'api_url';
  const activeEnvValue = form[envField] || fieldsMeta?.[envField]?.display || '';
  const envMode = useMemo(
    () => (envPresets ? detectEnvMode(activeEnvValue, envPresets) : null),
    [activeEnvValue, envPresets]
  );

  const setEnvMode = (mode) => {
    if (!envPresets?.[mode]) return;
    setForm((prev) => ({ ...prev, [envField]: envPresets[mode] }));
  };

  const handleSave = async () => {
    if (!writable) {
      toast.error(writeHint || 'Save credentials on digital.flare.pk only.');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && String(v).trim() !== '') payload[k] = String(v).trim();
      });
      // Always persist env preset (API URL or JazzCash environment)
      if (envPresets && envMode && envPresets[envMode]) {
        payload[envField] = envPresets[envMode];
      }
      await onSave(payload);
      toast.success(`${title} saved (encrypted in database).`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest(testTo);
    } finally {
      setTesting(false);
    }
  };

  const handleConnectTest = async () => {
    if (!onConnectTest) return;
    setConnecting(true);
    setConnectResult(null);
    try {
      const result = await onConnectTest();
      setConnectResult(result);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden transition-shadow ${
        open ? 'border-gray-300 shadow-sm' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left hover:bg-gray-50/80"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {envMode === 'testing' && (
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                Testing
              </span>
            )}
            {envMode === 'live' && (
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900">
                Live
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              ready ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
            }`}
          >
            {ready ? 'Configured' : 'Not ready'}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open ? (
        <>
          <div className="mx-5 mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
            <div className="font-semibold text-gray-800 mb-2">Currently active</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              {fieldDefs.map((f) => {
                const meta = fieldsMeta?.[f.key];
                const shown = f.secret ? (meta?.display || meta?.masked || '—') : (meta?.display || '—');
                return (
                  <div key={`cur-${f.key}`} className="flex justify-between gap-3 py-1 border-b border-gray-100">
                    <span className="text-gray-500">{f.label}</span>
                    <span className="font-medium text-gray-900 text-right break-all">
                      {shown}
                      {meta?.source && meta.source !== 'none' ? (
                        <span className="ml-2 text-[11px] uppercase text-gray-400">{meta.source}</span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-5 pb-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {envPresets ? (
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Environment</span>
                  <select
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 disabled:bg-gray-100 bg-white"
                    value={envMode || 'live'}
                    onChange={(e) => setEnvMode(e.target.value)}
                    disabled={!writable}
                  >
                    <option value="live">{testLabel === 'jazzcash' ? 'Live (production merchant)' : 'Live (production API)'}</option>
                    <option value="testing">{testLabel === 'jazzcash' ? 'Sandbox (JazzCash sandbox)' : 'Testing (staging API)'}</option>
                  </select>
                  <span className="mt-1 block text-xs text-gray-500 break-all">
                    {envPresets[envMode || 'live']}
                  </span>
                </label>
                {testLabel === 'jazzcash' ? (
                  envMode === 'testing' ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      Sandbox uses JazzCash sandbox Merchant ID / Password / Integrity Salt.
                      Return URL and IPN must match the domain registered with JazzCash (digital.flare.pk).
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-950">
                      Live charges real cards / JazzCash wallets. Save credentials, then Test connection before checkout.
                    </div>
                  )
                ) : envMode === 'testing' ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                    Staging rejects your <strong>live</strong> API key (error: Invalid API Key).
                    Use Leopards portal staging credentials here, or switch to <strong>Live</strong> for real CN booking.
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-950">
                    Live uses your merchant portal API key + password. Save, then Test connection before printing slips.
                  </div>
                )}
              </div>
            ) : null}

            {fieldDefs.map((f) => {
              if (envPresets && f.key === envField) return null;
              const meta = fieldsMeta?.[f.key];
              return (
                <label key={f.key} className="block text-sm">
                  <span className="font-medium text-gray-700">{f.label}</span>
                  {f.secret && meta?.display && (
                    <span className="ml-2 text-xs text-gray-400">saved {meta.display} — leave blank to keep</span>
                  )}
                  <input
                    type={f.secret ? 'password' : 'text'}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 disabled:bg-gray-100"
                    placeholder={
                      !writable
                        ? 'Edit on digital.flare.pk'
                        : f.secret
                          ? 'Leave blank to keep existing'
                          : f.placeholder
                    }
                    value={form[f.key] || ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    autoComplete="off"
                    disabled={!writable}
                  />
                </label>
              );
            })}
          </div>

          <div className="px-5 pb-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !writable}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
              style={{ backgroundColor: FLARE_RED }}
            >
              {saving ? 'Saving…' : writable ? 'Save to Database' : 'Save on live site only'}
            </button>
            {testLabel === 'mail' && (
              <>
                <button
                  type="button"
                  onClick={handleConnectTest}
                  disabled={connecting}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold disabled:opacity-60"
                >
                  {connecting ? 'Connecting…' : 'Test AWS connection'}
                </button>
                <input
                  type="email"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="test@email.com"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing || !testTo}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold disabled:opacity-60"
                >
                  {testing ? 'Sending…' : 'Send test email'}
                </button>
              </>
            )}
            {(testLabel === 'tcs' || testLabel === 'leopards' || testLabel === 'jazzcash') && (
              <button
                type="button"
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold disabled:opacity-60"
              >
                {testing
                  ? 'Testing…'
                  : testLabel === 'leopards'
                    ? 'Test Leopards connection'
                    : testLabel === 'jazzcash'
                      ? 'Test JazzCash connection'
                      : 'Test TCS connection'}
              </button>
            )}
            {testLabel === 'leopards' && typeof onSyncCities === 'function' && (
              <button
                type="button"
                onClick={async () => {
                  setTesting(true);
                  try {
                    await onSyncCities();
                  } finally {
                    setTesting(false);
                  }
                }}
                disabled={testing || !writable}
                className="px-4 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-900 text-sm font-semibold disabled:opacity-60"
              >
                {testing ? 'Importing…' : 'Import all cities to database'}
              </button>
            )}
          </div>

          {connectResult ? (
            <div
              className={`mx-5 mb-5 rounded-lg border px-4 py-3 text-sm ${
                connectResult.ok
                  ? 'border-green-200 bg-green-50 text-green-900'
                  : 'border-red-200 bg-red-50 text-red-900'
              }`}
            >
              <div className="font-semibold">{connectResult.message}</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                {[
                  ['Host', connectResult.checks?.host],
                  ['Port', connectResult.checks?.port],
                  ['Username saved', connectResult.checks?.username],
                  ['Password saved', connectResult.checks?.password],
                  ['From address', connectResult.checks?.from_address],
                  ['SES endpoint', connectResult.checks?.ses_endpoint],
                  ['SMTP login', connectResult.checks?.auth],
                ].map(([label, ok]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <span>{label}</span>
                    <span className="font-semibold">{ok ? 'OK' : 'Missing / failed'}</span>
                  </div>
                ))}
              </div>
              {connectResult.region ? (
                <div className="mt-2 text-xs opacity-80">Region: {connectResult.region}</div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default function PluginsSection() {
  const [plugins, setPlugins] = useState(null);
  const [writable, setWritable] = useState(true);
  const [writeHint, setWriteHint] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await pluginsApi.get();
      setPlugins(data.plugins || {});
      setWritable(data.writable !== false);
      setWriteHint(data.write_hint || '');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <LoadingState label="Loading plugins" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="admin-page-title">Plugins & Integrations</h1>
          <p className="admin-page-sub">
            Open one plugin at a time to edit. Courier plugins have Live / Testing mode.
            SES credentials can only be changed on the live admin (digital.flare.pk).
          </p>
          <p className="text-sm mt-2">
            Leopards books CN for <strong>paid website orders</strong> on print slip.
            Complimentary packing stays manual on{' '}
            <Link to="/magazine/packing" className="font-semibold underline" style={{ color: FLARE_RED }}>
              Packing Desk
            </Link>
            .
          </p>
        </div>
      </div>

      {!writable && writeHint ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {writeHint}
        </div>
      ) : null}

      <PluginCard
        id="ses_smtp"
        open={openId === 'ses_smtp'}
        onToggle={toggle}
        title="Email (AWS SES / SMTP)"
        description="OTP, dispatch, contact, and other emails."
        ready={!!plugins?.ses_smtp?.ready}
        fieldsMeta={plugins?.ses_smtp?.fields}
        fieldDefs={SES_FIELDS}
        testLabel="mail"
        writable={writable}
        writeHint={writeHint}
        onSave={async (fields) => {
          const { data } = await pluginsApi.update('ses_smtp', fields);
          setPlugins(data.plugins || {});
          setWritable(data.writable !== false);
          setWriteHint(data.write_hint || '');
        }}
        onTest={async (to) => {
          try {
            const { data } = await pluginsApi.testMail(to);
            if (data.ok) toast.success(data.message);
            else toast.error(data.message || 'Test failed');
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          }
        }}
        onConnectTest={async () => {
          try {
            const { data } = await pluginsApi.testSes();
            if (data.ok) toast.success(data.message || 'AWS SES connected');
            else toast.error(data.message || 'AWS connection failed');
            return data;
          } catch (e) {
            const data = e?.response?.data || {};
            toast.error(data.message || e.message || 'AWS connection failed');
            return {
              ok: false,
              message: data.message || e.message || 'AWS connection failed',
              checks: data.checks || {},
              region: data.region || null,
            };
          }
        }}
      />

      <PluginCard
        id="jazzcash"
        open={openId === 'jazzcash'}
        onToggle={toggle}
        title="JazzCash Payment Gateway"
        description="Card / JazzCash checkout (Page Redirection + IPN). Password and Integrity Salt are encrypted in the database."
        ready={!!plugins?.jazzcash?.ready}
        fieldsMeta={plugins?.jazzcash?.fields}
        fieldDefs={JAZZCASH_FIELDS}
        testLabel="jazzcash"
        envPresets={ENV_PRESETS.jazzcash}
        writable={writable}
        writeHint={writeHint}
        onSave={async (fields) => {
          const { data } = await pluginsApi.update('jazzcash', fields);
          setPlugins(data.plugins || {});
          setWritable(data.writable !== false);
          setWriteHint(data.write_hint || '');
        }}
        onTest={async () => {
          try {
            const { data } = await pluginsApi.testJazzCash();
            if (data.ok) toast.success(data.message || 'JazzCash OK');
            else toast.error(data.message || 'JazzCash test failed');
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          }
        }}
      />

      <PluginCard
        id="leopards"
        open={openId === 'leopards'}
        onToggle={toggle}
        title="Leopards Courier"
        description="Paid website orders book CN on Print preview. Free / complimentary stays manual."
        ready={!!plugins?.leopards?.ready}
        fieldsMeta={plugins?.leopards?.fields}
        fieldDefs={LEOPARDS_FIELDS}
        testLabel="leopards"
        envPresets={ENV_PRESETS.leopards}
        writable={writable}
        writeHint={writeHint}
        onSave={async (fields) => {
          const { data } = await pluginsApi.update('leopards', fields);
          setPlugins(data.plugins || {});
          setWritable(data.writable !== false);
          setWriteHint(data.write_hint || '');
        }}
        onTest={async () => {
          try {
            const { data } = await pluginsApi.testLeopards();
            if (data.ok) toast.success(data.message || 'Leopards OK');
            else toast.error(data.message || 'Leopards test failed');
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          }
        }}
        onSyncCities={async () => {
          try {
            const { data } = await pluginsApi.syncLeopardsCities();
            if (data.ok) toast.success(data.message || `Imported ${data.count || 0} cities`);
            else toast.error(data.message || 'City import failed');
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          }
        }}
      />

      <PluginCard
        id="tcs"
        open={openId === 'tcs'}
        onToggle={toggle}
        title="TCS Courier"
        description="Optional legacy courier for Packing Desk. Prefer Leopards for paid website orders."
        ready={!!plugins?.tcs?.ready}
        fieldsMeta={plugins?.tcs?.fields}
        fieldDefs={TCS_FIELDS}
        testLabel="tcs"
        envPresets={ENV_PRESETS.tcs}
        writable={writable}
        writeHint={writeHint}
        onSave={async (fields) => {
          const { data } = await pluginsApi.update('tcs', fields);
          setPlugins(data.plugins || {});
          setWritable(data.writable !== false);
          setWriteHint(data.write_hint || '');
        }}
        onTest={async () => {
          try {
            const { data } = await pluginsApi.testTcs();
            if (data.ok) toast.success(data.message || 'TCS OK');
            else toast.error(data.message || 'TCS test failed');
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          }
        }}
      />
    </div>
  );
}
